<template>
  <div class="shipment-rate-shop-tab" :class="tab.key">
    <RateShopBenchmarkRow
      v-model:freight-allowance="freightAllowanceInternal"
      :benchmarks="benchmarks"
      :rate-shop="rateShop"
      :show-freight-allowance="shouldShowFreightAllowance"
      :freight-allowance-editable="isFreightAllowanceEditable"
    />

    <Message
      v-if="shouldShowMissingLtlInfoBanner"
      severity="info"
      class="rygen-message"
    >
      {{ t('lineItems.missingLtlInfoMessage') }}
    </Message>

    <RoutingGuide
      v-if="routingGuideRule"
      id="routing-guide"
      :rate-shop="rateShop"
      :accepted-rate-quote="acceptedRateQuote"
      :service-type-filters="serviceTypeFilters"
      :service-type-filter-set="isAnyServiceTypeFilterSet"
      disabled
    />
    <RateShopTable
      v-else
      id="other-rates"
      :rate-quotes="additionalRateQuotes"
      :loading="rateShopLoading"
    >
      <template #empty>
        {{ t('common.noParam', { param: t('rateShop.rates', 2) }) }}
      </template>
    </RateShopTable>
  </div>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue';
import { computed, toRef, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { Loadable } from '@rygen/rygen-ui/model';

import {
  useFreightAllowance,
  useRateShop,
  useRoutingGuide,
} from '@/composables';
import {
  FreightAllowance,
  RateShop,
  type ServiceTypeFilters,
  Shipment,
} from '@/model';

import RateShopBenchmarkRow from '@/components/rateShop/RateShopBenchmarkRow.vue';
import RateShopTable from '@/components/rateShop/RateShopTable.vue';
import RoutingGuide from '@/components/rateShop/RoutingGuide.vue';
import { ShipmentRateShopTab } from '@/components/shipment/rateShop/ShipmentRateShopHeader.vue';
import Message from 'primevue/message';

const serviceTypeFilters = defineModel('serviceTypeFilters', {
  type: Object as PropType<ServiceTypeFilters>,
  default: () => ({}),
});

const props = defineProps({
  tab: {
    type: Object as PropType<ShipmentRateShopTab>,
    required: true,
  },
  shipment: {
    type: Shipment,
    default: () => new Shipment(),
  },
  rateShop: {
    type: Object as PropType<Loadable<RateShop>>,
    default: () => ({ loading: false }),
  },
  rateShopLoading: {
    type: Boolean,
    default: () => false,
  },
  freightAllowance: {
    type: Object as PropType<FreightAllowance | undefined>,
    default: () => undefined,
  },
});
const { tab, shipment, rateShop, rateShopLoading, freightAllowance } =
  toRefs(props);
const emit = defineEmits<{
  (
    event: 'update:freight-allowance',
    freightAllowance?: FreightAllowance,
  ): void;
}>();

const {
  freightAllowanceInternal,
  isFreightAllowanceEditable,
  shouldShowFreightAllowance,
} = useFreightAllowance(freightAllowance, emit);
const { t } = useI18n();
const {
  acceptedRateQuote,
  additionalRateQuotes,
  benchmarks,
  isAnyServiceTypeFilterSet,
} = useRateShop(rateShop, {
  serviceTypeFilters,
  acceptedRateQuoteId: toRef(() => shipment.value.acceptedRateQuoteId),
});
const { routingGuideRule } = useRoutingGuide(rateShop, serviceTypeFilters);

const lineItemsMissingLtlInfo = computed(() => {
  const lineItemsMissingFreightClass = shipment.value.lineItems.some(
    (li) => !li.freightClass,
  );
  const lineItemsMissingDimensions = shipment.value.lineItems.some(
    (li) => !li.heightDim || !li.widthDim || !li.lengthDim,
  );
  return lineItemsMissingDimensions || lineItemsMissingFreightClass;
});

const shouldShowMissingLtlInfoBanner = computed(() => {
  return lineItemsMissingLtlInfo.value && !rateShopLoading.value;
});
</script>
